import { ImageLoaderProps } from 'next/image';

// Our on-the-fly image CDN (AWS Dynamic Image Transformation for CloudFront)
// resizes the original via query params, e.g. .../GH2326_...jpg?format=webp&width=800.
// The CDN serves the same assets under every brand alias and negotiates the
// delivered format (AVIF → WebP → JPEG) on the browser's Accept header regardless
// of the `format` we send, so we just ask for resize + width here.
//
// EWM-453: the Electus storefront emits its own region media host —
// en-AU → media.electusdistribution.com.au, en-NZ → media.electusdistribution.co.nz —
// rather than the raw `media.jaycar.com.au` host baked into commercetools/Algolia
// data. next/image invokes a loader as a pure ({ src, width }) function with no
// locale, so the Image atom binds one per render via `createMediaLoader(locale)`.
//
// Any source on our media CDN (any brand alias) is recognised and rewritten to the
// region host; every other source (S3 originals, Cloudinary raw URLs, local
// `/sb-assets/*`, `data:`/`blob:`) passes through unchanged.

const MEDIA_HOST_BY_LOCALE: Record<string, string> = {
  'en-AU': 'media.electusdistribution.com.au',
  'en-NZ': 'media.electusdistribution.co.nz',
};
const DEFAULT_MEDIA_HOST = MEDIA_HOST_BY_LOCALE['en-AU'];

// Hosts that resolve to our image CDN (all brand aliases) — recognised as
// resizable and rewritten to the region host above.
const MEDIA_CDN_HOSTS = [
  'media.jaycar.com.au',
  'media.jaycar.co.nz',
  'media.roadtechmarine.com.au',
  'media.electusdistribution.com.au',
  'media.electusdistribution.co.nz',
];

export const mediaHostForLocale = (locale: string): string => MEDIA_HOST_BY_LOCALE[locale] ?? DEFAULT_MEDIA_HOST;

export const createMediaLoader =
  (locale: string) =>
  ({ src, width }: ImageLoaderProps): string => {
    let url: URL;
    try {
      url = new URL(src);
    } catch {
      return src; // relative/local (`/sb-assets/*`) or malformed → passthrough
    }
    if (!MEDIA_CDN_HOSTS.includes(url.hostname)) {
      return src; // not our CDN (S3, Cloudinary, data:/blob:) → passthrough
    }

    url.hostname = mediaHostForLocale(locale);
    url.searchParams.set('format', 'webp'); // CDN negotiates AVIF/WebP/JPEG on Accept regardless
    url.searchParams.set('width', String(width));
    return url.toString();
  };
